import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { timeCategoriesApi, timesheetApi } from '../tasks.api';
import type { TimeEntryCategory } from '../tasks.types';
import { errorMessage, today } from '../tasks.utils';
import DialogShell from './DialogShell';

export interface LoggableTask {
  id: number;
  label: string;
}

/**
 * Log time by hand: a start/end pair or plain hours, against a task — or, opened with
 * `target='other'`, for work outside any task (a meeting, training, office work) against a
 * category. The two have their own buttons where the dialog is opened, so it never asks.
 */
export default function TimeEntryDialog({
  open,
  tasks,
  taskId,
  date,
  target = 'task',
  onClose,
  onDone,
}: {
  open: boolean;
  tasks: LoggableTask[];
  taskId?: number | null;
  date?: string;
  target?: 'task' | 'other';
  onClose: () => void;
  onDone: () => void;
}) {
  const [task, setTask] = useState<number | ''>('');
  const [categories, setCategories] = useState<TimeEntryCategory[]>([]);
  const [category, setCategory] = useState<number | ''>('');
  const [workDate, setWorkDate] = useState(today());
  const [mode, setMode] = useState<'range' | 'hours'>('range');
  const [start, setStart] = useState('09:30');
  const [end, setEnd] = useState('11:00');
  const [hours, setHours] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTask(taskId ?? tasks[0]?.id ?? '');
    setCategory('');
    setWorkDate(date ?? today());
    setNotes('');
    setHours('');
  }, [open, taskId, date, tasks]);

  // Only active categories are offered; the list is fetched each time the dialog opens.
  useEffect(() => {
    if (!open || target !== 'other') return;
    let live = true;
    timeCategoriesApi
      .list()
      .then((list) => live && setCategories(list.filter((c) => c.isActive)))
      .catch((e) => toast.error(errorMessage(e, 'Could not load the categories')));
    return () => {
      live = false;
    };
  }, [open, target]);

  const submit = async () => {
    if (target === 'task' && !task) return toast.error('Choose a task.');
    if (target === 'other' && !category) return toast.error('Choose what the time was spent on.');
    setBusy(true);
    try {
      await timesheetApi.log({
        ...(target === 'task' ? { taskId: Number(task) } : { categoryId: Number(category) }),
        workDate,
        notes: notes || undefined,
        ...(mode === 'range' ? { startTime: start, endTime: end } : { hours: Number(hours) }),
      });
      toast.success('Time logged');
      onDone();
    } catch (e) {
      toast.error(errorMessage(e, 'Could not log time'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogShell open={open} title={target === 'other' ? 'LOG OTHER WORK' : 'LOG TIME'} onClose={onClose} onSubmit={submit} submitLabel='Log' busy={busy}>
      {target === 'task' ? (
        <TextField select label='Task' size='small' value={task} onChange={(e) => setTask(Number(e.target.value))}>
          {tasks.map((t) => (
            <MenuItem key={t.id} value={t.id}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
      ) : (
        <TextField select label='Category' size='small' value={category} onChange={(e) => setCategory(Number(e.target.value))}
          helperText='Work outside a task, e.g. a meeting or training. It counts on your timesheet, not on any task.'>
          {categories.map((c) => (
            <MenuItem key={c.id} value={c.id}>
              {c.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      <TextField label='Date' type='date' size='small' InputLabelProps={{ shrink: true }} inputProps={{ max: today() }} value={workDate} onChange={(e) => setWorkDate(e.target.value)} />
      <ToggleButtonGroup size='small' exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
        <ToggleButton value='range'>From – to</ToggleButton>
        <ToggleButton value='hours'>Hours</ToggleButton>
      </ToggleButtonGroup>
      {mode === 'range' ? (
        <Stack direction='row' gap={2}>
          <TextField label='From' type='time' size='small' fullWidth InputLabelProps={{ shrink: true }} value={start} onChange={(e) => setStart(e.target.value)} />
          <TextField label='To' type='time' size='small' fullWidth InputLabelProps={{ shrink: true }} value={end} onChange={(e) => setEnd(e.target.value)} />
        </Stack>
      ) : (
        <TextField label='Hours' type='number' size='small' inputProps={{ min: 0.25, step: 0.25, max: 24 }} value={hours} onChange={(e) => setHours(e.target.value)} />
      )}
      <TextField label='Notes' size='small' value={notes} onChange={(e) => setNotes(e.target.value)}
        placeholder={target === 'other' ? 'e.g. Weekly review with the design team' : undefined} />
    </DialogShell>
  );
}
